import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'ds-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModalComponent implements OnInit, OnChanges {

  @Input() focusCloseButtonOnChange: boolean = true;
  @Input() initCloseButtonFocused: boolean = false;
  @Input() modalIsActive: boolean;
  @Input() modalHeader: string;
  @Input() initiator: any;
  @Input() isStoreHeaderShown: boolean = false;
  @Output() canBeClosed = new EventEmitter();
  @ViewChild('modal') modalElement: ElementRef;
  @ViewChild('modalBody') modalBodyElement: ElementRef;
  @ViewChild('modalCloseButton') modalCloseButtonElement: ElementRef;

  escapeKeyCode = 'Escape';
  previousValidEscapeKeyDown: boolean;
  leftMouseButtonNumber = 0;
  previousValidMouseDown: boolean;
  previousValidMouseUp: boolean;

  constructor(
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) public platformId: string,
    ) {
  }

  ngOnInit(): void {
    this.resetValidKeyBoardEvents();
    this.resetValidMouseEvents();
    if(this.initCloseButtonFocused) {
      this.focusModalCloseButton();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.modalIsActive && !changes.modalIsActive.firstChange && changes.modalIsActive.currentValue) {
        this.resetValidKeyBoardEvents();
        this.resetValidMouseEvents();
        this.focusModalCloseButton();
        this.adjustScrolling(true);
      }
      if (changes.modalIsActive && !changes.modalIsActive.currentValue) {
        this.closeModal();
      }
    }
  }

  /**
   * Prevents/enables body scrolling when modal is opened/closed
   *
   * @disableScrolling - boolean, if true disables scrolling of body
   */
  adjustScrolling(disableScrolling: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const body = document.querySelector('body');
    const modalOpenClass = 'body--modal-open';
    if (disableScrolling) {
      this.renderer.addClass(body, modalOpenClass);
    } else {
      this.renderer.removeClass(body, modalOpenClass);
    }
  }

  /**
   * Closes modal and emits event (event can be used e.g. regaining focus of initiator)
   */
  closeModal(): void {
    this.adjustScrolling(false);
    if (!this.modalIsActive) {
      this.modalIsActive = false;
    }
    this.canBeClosed.emit(this.initiator ? this.initiator : null);
  }

  /**
   * Resets valid keyboard event booleans
   */
  resetValidKeyBoardEvents(): void {
    this.previousValidEscapeKeyDown = false;
  }

  /**
   * Resets valid mouse event booleans
   */
  resetValidMouseEvents(): void {
    this.previousValidMouseDown = false;
    this.previousValidMouseUp = false;
  }

  /**
   * Returns true if event is a user initiated event
   */
  trustedEvent(event: Event): boolean {
    return event.isTrusted;
  }

  /**
   * Decides if KeyboardEvent event is valid for our case:
   * checks if keycode is the escape key
   * checks if event is a trusted event
   *
   * @param event - KeyboardEvent which should be processed
   */
  isValidModalCloseKeyboardEvent(event: KeyboardEvent): boolean {
    return event.code === this.escapeKeyCode && this.trustedEvent(event);
  }

  /**
   * Sets boolean if keydown event is valid
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.modalIsActive) {
      return;
    }
    this.previousValidEscapeKeyDown = this.isValidModalCloseKeyboardEvent(event);
  }

  /**
   * Determines if keyup event is valid and closes modal
   * Aborts action if there was no previous valid keydown event
   */
  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent): void {
    if (!this.modalIsActive || !this.previousValidEscapeKeyDown) {
      return;
    }
    if (this.isValidModalCloseKeyboardEvent(event)) {
      this.resetValidKeyBoardEvents();
      this.closeModal();
    }
  }

  /**
   * Decides if MouseEvent is valid for our case:
   * checks button type
   * checks if MouseEvent is a trusted event
   * checks if the event target is the modal background element
   */
  isValidModalCloseClickEvent(event: MouseEvent): boolean {
    return event.button === this.leftMouseButtonNumber && this.trustedEvent(event) && event.target === this.modalElement.nativeElement;
  }

  /**
   * Sets boolean if mousedown event is valid
   */
  handleMouseDownEvent(event: MouseEvent): void {
    if (!this.modalIsActive) {
      return;
    }
    this.previousValidMouseDown = this.isValidModalCloseClickEvent(event);
  }

  /**
   * Sets boolean if mouseup event is valid
   * Aborts action if there was no previous valid mousedown event
   */
  handleMouseUpEvent(event: MouseEvent): void {
    if (!this.modalIsActive || !this.previousValidMouseDown) {
      return;
    }
    this.previousValidMouseUp = this.isValidModalCloseClickEvent(event);
  }

  /**
   * Determines if click event is valid and closes modal
   * Aborts action if there was no previous valid mousedown/mouseup event
   */
  handleClickEvent(event: MouseEvent): void {
    if (!this.modalIsActive || !this.previousValidMouseDown || !this.previousValidMouseUp) {
      return;
    }
    if (this.modalElement && this.isValidModalCloseClickEvent(event)) {
      this.resetValidKeyBoardEvents();
      this.closeModal();
    }
  }

  /**
   * Sets focus on the modal close button
   */
  public focusModalCloseButton(): void {
    if (this.focusCloseButtonOnChange) {
      setTimeout(() => {
        if (this.modalIsActive && this.modalCloseButtonElement) {
          this.modalCloseButtonElement.nativeElement.focus();
        }
      });
    }
  }
}
